import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import PasswordStrength, { isPasswordStrong } from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useT();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) {
      toast.error(t("rp_mismatch"));
      return;
    }
    if (!isPasswordStrong(pw1)) {
      toast.error("Password is too weak. Please meet all 5 requirements below.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw1 });
      if (data?.access_token && setSession) {
        setSession(data.access_token, data.user);
      }
      toast.success(t("rp_success"));
      navigate("/app", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "weak_password") {
        toast.error(detail.message || "Password is too weak.");
      } else {
        toast.error(typeof detail === "string" ? detail : t("rp_invalid"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="reset-password-no-token">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-black tracking-tighter">{t("rp_no_token_title")}</h1>
          <p className="mt-2 text-zinc-600">{t("rp_no_token_body")}</p>
          <Link to="/forgot-password" className="mt-6 inline-flex btn-accent px-5 py-2.5 font-semibold rounded-md">
            {t("rp_request_new")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="reset-password-page">
      <div className="flex flex-col px-6 sm:px-12 py-8 max-w-md mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex"><Logo size="md" /></Link>
          <LangToggle variant="outline" />
        </div>

        <div className="mt-16">
          <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-blue-100">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="mt-6 text-4xl font-black tracking-tighter">{t("rp_title")}</h1>
          <p className="mt-3 text-zinc-600">{t("rp_subtitle")}</p>

          <form onSubmit={submit} className="mt-10 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                {t("rp_new_password")}
              </label>
              <PasswordInput
                data-testid="rp-pw1-input"
                testidEye="rp-pw1-eye"
                required minLength={8} autoComplete="new-password"
                value={pw1} onChange={(e) => setPw1(e.target.value)}
                className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
              />
              <PasswordStrength password={pw1} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                {t("rp_confirm_password")}
              </label>
              <PasswordInput
                data-testid="rp-pw2-input"
                testidEye="rp-pw2-eye"
                required minLength={8} autoComplete="new-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="mt-1 w-full px-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-md"
              />
            </div>
            <button
              type="submit" data-testid="rp-submit-btn" disabled={loading}
              className="btn-accent w-full inline-flex items-center justify-center gap-2 font-semibold py-3.5 rounded-md disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>{t("rp_submit_btn")} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>

          <div className="mt-8">
            <Link to="/auth" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-blue-600" data-testid="rp-back-link">
              <ArrowLeft className="w-4 h-4" /> {t("fp_back_login")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
